// src/pages/PortalLayout.js

import { useState } from 'react';
import { NavLink, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Car, CreditCard, User, LogOut, Menu, X,
  Building2, ShieldCheck, DollarSign, Tag, Users, Settings,
  Navigation, BarChart2, MapPin,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ui/ThemeToggle';
import Dashboard from './Dashboard';
import Upgrade from './Upgrade';
import Account from './Account';
import Dealers from './Dealers';
import Trips from './Trips';
import TripDetail from './TripDetail';
import Reports from './Reports';
import AdminPayments from './AdminPayments';
import AdminVouchers from './AdminVouchers';
import AdminUsers from './AdminUsers';
import AdminSettings from './AdminSettings';
import Zones from './Zones';

// Individual user nav
const userNavItems = [
  { to: '/portal',          label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/portal/trips',    label: 'Trips',     icon: Navigation },
  { to: '/portal/zones',    label: 'Zones',     icon: MapPin },
  { to: '/portal/reports',  label: 'Reports',   icon: BarChart2 },
  { to: '/portal/upgrade',  label: 'Upgrade',   icon: CreditCard },
  { to: '/portal/account',  label: 'Account',   icon: User },
];

// Dealer nav
const dealerNavItems = [
  { to: '/portal',          label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/portal/dealers',  label: 'Business',  icon: Building2 },
  { to: '/portal/account',  label: 'Account',   icon: User },
];

const adminNavItems = [
  { to: '/portal/admin/payments', label: 'Payments', icon: DollarSign },
  { to: '/portal/admin/vouchers', label: 'Vouchers',  icon: Tag },
  { to: '/portal/admin/users',    label: 'Users',     icon: Users },
  { to: '/portal/admin/settings', label: 'Settings',  icon: Settings },
];

export default function PortalLayout() {
  const theme    = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isDealer = !!user?.voucher_batch_id;
  const isAdmin  = !!user?.is_super_admin;
  const navItems = isDealer ? dealerNavItems : userNavItems;

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  const Sidebar = ({ mobile = false }) => (
    <div
      className={`flex flex-col h-screen ${mobile ? 'w-full' : 'w-64'}`}
      style={{ backgroundColor: theme.card, borderRight: `1px solid ${theme.border}` }}
    >
      {/* Logo */}
      <div className="flex items-center justify-between px-6 py-5"
        style={{ borderBottom: `1px solid ${theme.border}` }}>
        <img src="/logo.png" alt="TripData" className="h-7 object-contain" />
        {mobile && (
          <button onClick={() => setSidebarOpen(false)} style={{ color: theme.text }}>
            <X size={22} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? 'bg-gradient-brand text-white' : 'hover:opacity-70'
              }`
            }
            style={({ isActive }) => ({ color: isActive ? '#fff' : theme.textSecondary })}
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}

        {/* Admin section */}
        {isAdmin && (
          <>
            <div className="pt-3 pb-1 px-3">
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={11} style={{ color: theme.textMuted }} />
                <p className="text-[10px] font-bold uppercase tracking-widest"
                  style={{ color: theme.textMuted }}>Admin</p>
              </div>
            </div>
            {adminNavItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive ? 'bg-gradient-brand text-white' : 'hover:opacity-70'
                  }`
                }
                style={({ isActive }) => ({ color: isActive ? '#fff' : theme.textSecondary })}
              >
                <Icon size={18} />
                {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User + logout */}
      <div className="px-4 py-4" style={{ borderTop: `1px solid ${theme.border}` }}>
        <div className="flex items-center gap-3 mb-3 px-1">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-brand shrink-0">
            {(user?.display_name || user?.email || '?')[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" style={{ color: theme.text }}>
              {user?.display_name || 'User'}
            </p>
            <p className="text-xs truncate" style={{ color: theme.textMuted }}>{user?.email}</p>
          </div>
          <ThemeToggle />
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 w-full rounded-xl text-sm transition-colors hover:opacity-70"
          style={{ color: theme.textSecondary }}
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex overflow-x-hidden" style={{ backgroundColor: theme.background }}>

      {/* Desktop sidebar */}
      <div className="hidden md:block fixed top-0 left-0 h-screen w-64 z-30 overflow-hidden">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="w-64 max-w-[80vw]">
            <Sidebar mobile />
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0">

        {/* Mobile top bar */}
        <div
          className="md:hidden flex items-center justify-between px-4 py-3 sticky top-0 z-20"
          style={{ backgroundColor: theme.card, borderBottom: `1px solid ${theme.border}` }}
        >
          <button onClick={() => setSidebarOpen(true)} style={{ color: theme.text }}>
            <Menu size={22} />
          </button>
          <img src="/logo.png" alt="TripData" className="h-6 object-contain" />
          <ThemeToggle />
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-8 min-w-0 overflow-x-hidden">
          <Routes>
            <Route index                        element={<Dashboard />} />
            <Route path="trips"                 element={<Trips />} />
            <Route path="trips/:id"             element={<TripDetail />} />
            <Route path="zones"                 element={<Zones />} />
            <Route path="reports"               element={<Reports />} />
            <Route path="upgrade"               element={<Upgrade />} />
            <Route path="account"               element={<Account />} />
            <Route path="dealers"               element={<Dealers />} />
            <Route path="admin/payments"        element={<AdminPayments />} />
            <Route path="admin/vouchers"        element={<AdminVouchers />} />
            <Route path="admin/users"           element={<AdminUsers />} />
            <Route path="admin/settings"        element={<AdminSettings />} />
            <Route path="*"                     element={<Navigate to="/portal" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}